/**
 * Body scroll prevention utility that works reliably on mobile Safari
 * Replaces the problematic body-scroll-lock library
 */

interface ScrollLockOptions {
  reserveScrollBarGap?: boolean;
  allowTouchMove?: boolean;
}

class BodyScrollLock {
  private isLocked = false;
  private scrollY = 0;
  private originalStyles: { [key: string]: string } = {};
  private originalBodyStyles: { [key: string]: string } = {};

  disable(targetElement?: HTMLElement, options: ScrollLockOptions = {}) {
    if (this.isLocked) return;

    const element = targetElement || document.body;
    this.scrollY = window.scrollY;

    // Store original styles
    this.originalStyles = {
      position: element.style.position,
      top: element.style.top,
      left: element.style.left,
      right: element.style.right,
      width: element.style.width,
      height: element.style.height,
      overflow: element.style.overflow,
      "overflow-x": element.style.overflowX,
      "overflow-y": element.style.overflowY,
      "touch-action": element.style.touchAction,
      "-webkit-overflow-scrolling": (element.style as any)["-webkit-overflow-scrolling"],
    };

    this.originalBodyStyles = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      height: document.body.style.height,
      overflow: document.body.style.overflow,
      "overflow-x": document.body.style.overflowX,
      "overflow-y": document.body.style.overflowY,
      "touch-action": document.body.style.touchAction,
      "-webkit-overflow-scrolling": (document.body.style as any)["-webkit-overflow-scrolling"],
    };

    // Apply lock styles
    if (element === document.body) {
      // Lock body scroll
      document.body.style.position = "fixed";
      document.body.style.top = `-${this.scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
      document.body.style.height = "100%";
      document.body.style.overflow = "hidden";
      document.body.style["overflow-x"] = "hidden";
      document.body.style["overflow-y"] = "hidden";
      document.body.style["touch-action"] = "none";
      (document.body.style as any)["-webkit-overflow-scrolling"] = "auto";
    } else {
      // Lock specific element
      element.style.position = "fixed";
      element.style.top = `-${this.scrollY}px`;
      element.style.left = "0";
      element.style.right = "0";
      element.style.width = "100%";
      element.style.height = "100%";
      element.style.overflow = "hidden";
      element.style["overflow-x"] = "hidden";
      element.style["overflow-y"] = "hidden";
      element.style["touch-action"] = options.allowTouchMove ? "pan-y" : "none";
      (element.style as any)["-webkit-overflow-scrolling"] = "auto";
    }

    this.isLocked = true;
  }

  enable(targetElement?: HTMLElement) {
    if (!this.isLocked) return;

    const element = targetElement || document.body;

    // Restore original styles
    if (element === document.body) {
      Object.keys(this.originalBodyStyles).forEach((property) => {
        if (this.originalBodyStyles[property] !== undefined) {
          (document.body.style as any)[property] = this.originalBodyStyles[property];
        } else {
          (document.body.style as any)[property] = "";
        }
      });

      // No need to restore html element styles since we don't modify them
    } else {
      Object.keys(this.originalStyles).forEach((property) => {
        if (this.originalStyles[property] !== undefined) {
          (element.style as any)[property] = this.originalStyles[property];
        } else {
          (element.style as any)[property] = "";
        }
      });
    }

    // Restore scroll position
    if (element === document.body) {
      window.scrollTo(0, this.scrollY);
    }

    this.isLocked = false;
  }

  isScrollLocked() {
    return this.isLocked;
  }
}

// Create singleton instance
const bodyScrollLock = new BodyScrollLock();

// Export functions that match the body-scroll-lock API
export const disableBodyScroll = (targetElement?: HTMLElement, options?: ScrollLockOptions) => {
  bodyScrollLock.disable(targetElement, options);
};

export const enableBodyScroll = (targetElement?: HTMLElement) => {
  bodyScrollLock.enable(targetElement);
};

// Export the class for advanced usage
export { BodyScrollLock };
export default bodyScrollLock;
